import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useT } from "../lib/i18n";
import { openBooking } from "../lib/booking";

const HERO_IMG = "/photos/office.jpg";

const ZURICH_IMG =
    "https://images.unsplash.com/photo-1554899199-f6d99e6be6f7?w=1600&q=85&auto=format&fit=crop";

const ADVISOR_IMG = "/matteo-casati.jpg";

const PREVIEW_SLUGS = ["italians-in-switzerland", "3a-contribution-limits-2026", "bvg-buyback-strategy"];

const PREVIEW_META = {
    en: {
        "italians-in-switzerland": { title: "Italians in Switzerland: getting your 3rd pillar right from year one", cat: "Expats", date: "Dec 2026" },
        "3a-contribution-limits-2026": { title: "Pillar 3a contribution limits for 2026", cat: "3rd Pillar", date: "Nov 2026" },
        "bvg-buyback-strategy": { title: "BVG buybacks: a quietly powerful tax lever", cat: "2nd Pillar", date: "Oct 2026" },
    },
    it: {
        "italians-in-switzerland": { title: "Italiani in Svizzera: impostare bene il 3° pilastro dal primo anno", cat: "Expat", date: "Dic 2026" },
        "3a-contribution-limits-2026": { title: "Pilastro 3a: limiti di contribuzione 2026", cat: "3° Pilastro", date: "Nov 2026" },
        "bvg-buyback-strategy": { title: "Riscatti LPP: una leva fiscale silenziosa ma potente", cat: "2° Pilastro", date: "Ott 2026" },
    },
    de: {
        "italians-in-switzerland": { title: "Neu in der Schweiz: die 3. Säule vom ersten Jahr an richtig aufsetzen", cat: "Expats", date: "Dez 2026" },
        "3a-contribution-limits-2026": { title: "Säule 3a: Beitragslimiten 2026", cat: "3. Säule", date: "Nov 2026" },
        "bvg-buyback-strategy": { title: "BVG-Einkäufe: ein leiser, kraftvoller Steuerhebel", cat: "2. Säule", date: "Okt 2026" },
    },
    es: {
        "italians-in-switzerland": { title: "Recién llegados a Suiza: cómo configurar bien el 3er pilar desde el primer año", cat: "Expats", date: "Dic 2026" },
        "3a-contribution-limits-2026": { title: "Pilar 3a: límites de contribución 2026", cat: "3er Pilar", date: "Nov 2026" },
        "bvg-buyback-strategy": { title: "Recompras LPP: una palanca fiscal silenciosa pero potente", cat: "2° Pilar", date: "Oct 2026" },
    },
};

export default function Home() {
    const { t, lang } = useT();
    const navigate = useNavigate();

    useEffect(() => {
        const titles = {
            en: "Casati Consulting, Swiss 2nd & 3rd Pillar Advisory",
            it: "Casati Consulting, Consulenza svizzera 2° e 3° pilastro",
            de: "Casati Consulting, Schweizer 2. & 3. Säule Beratung",
            es: "Casati Consulting, Asesoría suiza 2° y 3er pilar",
        };
        document.title = titles[lang] || titles.en;
    }, [lang]);

    const pm = PREVIEW_META[lang] || PREVIEW_META.en;

    return (
        <div className="bg-[#F9F9F7] text-[#1C1C1C]">
            {/* HERO */}
            <section className="relative overflow-hidden" data-testid="hero">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-10 md:pt-16 pb-20 md:pb-28">
                    <p
                        className="text-[10px] uppercase tracking-[0.32em] font-bold text-[#9B7A58] mb-10 animate-fade-in"
                        data-testid="welcome-strip"
                    >
                        {t.welcome.line}
                    </p>

                    <div className="grid grid-cols-12 gap-y-10 md:gap-x-8">
                        <div className="col-span-12 md:col-span-7 flex flex-col">
                            <h1
                                className="font-display mt-6 text-[44px] sm:text-6xl lg:text-[88px] leading-[0.92] tracking-tight font-medium animate-fade-up"
                                style={{ letterSpacing: "-0.035em" }}
                                data-testid="hero-title"
                            >
                                {t.hero.titleA}
                                <br />
                                {t.hero.titleB}
                                <br />
                                <span className="italic font-light">{t.hero.titleC}</span>
                                <br />
                                <span className="italic font-light text-[#9B7A58]">{t.hero.titleD}</span>
                            </h1>
                            <p
                                className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#1C1C1C]/75"
                                data-testid="hero-subtitle"
                            >
                                {t.hero.subtitle}
                            </p>
                            <p
                                className="mt-6 max-w-xl text-base md:text-lg italic font-light leading-relaxed border-l-2 pl-5"
                                style={{ borderColor: "#9B7A58", color: "#1C1C1C" }}
                                data-testid="hero-motto"
                            >
                                {t.hero.motto}
                            </p>
                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                <button
                                    type="button"
                                    onClick={() => openBooking(navigate)}
                                    className="group inline-flex items-center gap-2 px-6 h-12 bg-[#1C1C1C] text-[#F9F9F7] text-[12px] uppercase tracking-[0.22em] font-bold hover:bg-[#9B7A58] transition-colors duration-300"
                                    data-testid="hero-cta-primary"
                                >
                                    {t.cta.book}
                                    <ArrowUpRight
                                        size={16}
                                        strokeWidth={1.5}
                                        className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                                    />
                                </button>
                                <Link
                                    to="/services"
                                    className="link-underline inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-bold text-[#1C1C1C]/80 hover:text-[#1C1C1C]"
                                    data-testid="hero-cta-secondary"
                                >
                                    {t.cta.explore}
                                    <ArrowRight size={14} strokeWidth={1.5} />
                                </Link>
                            </div>
                        </div>

                        <div className="col-span-12 md:col-span-5 md:col-start-8 grid grid-cols-2 grid-rows-3 gap-3 sm:gap-4 h-[420px] sm:h-[520px]">
                            <div className="relative col-span-2 row-span-2 overflow-hidden grain">
                                <img
                                    src={HERO_IMG}
                                    alt="Casati Consulting office reception in Glattbrugg, Zürich"
                                    className="absolute inset-0 w-full h-full object-cover"
                                    data-testid="hero-image"
                                />
                                <div className="absolute inset-0 bg-[#1C1C1C]/10" />
                            </div>
                            <div className="border hair p-5 flex flex-col justify-between">
                                <p className="eyebrow">{t.hero.founded}</p>
                                <p
                                    className="font-display text-4xl font-medium tracking-tight"
                                    style={{ letterSpacing: "-0.03em" }}
                                    data-testid="hero-year"
                                >
                                    {t.hero.year}
                                </p>
                            </div>
                            <div className="bg-[#1C1C1C] text-[#F9F9F7] p-5 flex flex-col justify-between">
                                <p
                                    className="text-[10px] uppercase tracking-[0.22em] font-bold"
                                    style={{ color: "#9B7A58" }}
                                >
                                    {t.hero.focus}
                                </p>
                                <p
                                    className="font-display text-2xl font-medium tracking-tight leading-tight whitespace-pre-line"
                                    style={{ letterSpacing: "-0.02em" }}
                                    data-testid="hero-focus-value"
                                >
                                    {t.hero.focusValue}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="border-y hair">
                    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center md:text-left">
                        {[t.hero.stripA, t.hero.stripB, t.hero.stripC, t.hero.stripD].map(([h, s]) => (
                            <div key={h} className="flex flex-col">
                                <span className="font-display text-base font-medium">{h}</span>
                                <span className="text-xs text-[#1C1C1C]/55 mt-1">{s}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ADVISOR introduction */}
            <section className="py-20 md:py-28" data-testid="advisor-intro">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-y-10 md:gap-x-12 items-center">
                    <div className="col-span-12 md:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden bg-[#EAEAE6]">
                            <img src={ADVISOR_IMG} alt="Matteo Casati" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="col-span-12 md:col-span-6 md:col-start-7">
                        <p className="eyebrow">{t.home.advisorEyebrow}</p>
                        <h2
                            className="font-display mt-6 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium leading-[1.05]"
                            style={{ letterSpacing: "-0.025em" }}
                        >
                            {t.home.advisorTitle}
                        </h2>
                        <p className="mt-8 text-[#1C1C1C]/75 leading-relaxed">{t.home.advisorBody}</p>
                        <Link
                            to="/about"
                            className="mt-8 link-underline inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-bold"
                            data-testid="advisor-intro-cta"
                        >
                            {t.home.advisorCta}
                            <ArrowRight size={14} strokeWidth={1.5} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="py-24 md:py-32 border-t hair" data-testid="services-preview">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                    <div className="grid grid-cols-12 gap-8 mb-16 md:mb-20">
                        <div className="col-span-12 md:col-span-5">
                            <p className="eyebrow">{t.services.eyebrow}</p>
                            <h2
                                className="font-display mt-5 text-4xl sm:text-5xl tracking-tight font-medium"
                                style={{ letterSpacing: "-0.03em" }}
                            >
                                {t.services.title1}
                                <br />
                                <span className="text-[#9B7A58]">{t.services.title2}</span>
                            </h2>
                        </div>
                        <p className="col-span-12 md:col-span-6 md:col-start-7 text-[#1C1C1C]/70 leading-relaxed text-base md:text-lg self-end">
                            {t.services.intro}
                        </p>
                    </div>

                    <div className="border hair grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 -mx-px stagger">
                        {t.services.list.map((s) => (
                            <div
                                key={s.n}
                                className="group relative p-8 md:p-10 border-t md:border-t-0 md:border-l hair first:border-t-0 first:md:border-l-0 hover:bg-white transition-colors duration-300"
                                data-testid={`service-card-${s.n}`}
                            >
                                <div className="flex items-baseline justify-between">
                                    <span
                                        className="text-[10px] uppercase tracking-[0.22em] font-bold"
                                        style={{ color: "#9B7A58" }}
                                    >
                                        {s.n}
                                    </span>
                                    <ArrowUpRight
                                        size={18}
                                        strokeWidth={1.5}
                                        className="text-[#1C1C1C]/30 group-hover:text-[#1C1C1C] transition-colors"
                                    />
                                </div>
                                <h3
                                    className="font-display mt-10 text-2xl tracking-tight font-medium"
                                    style={{ letterSpacing: "-0.02em" }}
                                >
                                    {s.t}
                                </h3>
                                <p className="mt-4 text-[#1C1C1C]/70 leading-relaxed text-[15px]">{s.d}</p>
                            </div>
                        ))}
                        <div className="p-8 md:p-10 border-t md:border-t-0 md:border-l hair flex flex-col justify-between bg-[#1C1C1C] text-[#F9F9F7]">
                            <p
                                className="text-[10px] uppercase tracking-[0.22em] font-bold"
                                style={{ color: "#9B7A58" }}
                            >
                                {t.services.engageEyebrow}
                            </p>
                            <div>
                                <h3
                                    className="font-display text-2xl tracking-tight font-medium"
                                    style={{ letterSpacing: "-0.02em" }}
                                >
                                    {t.services.engage}
                                </h3>
                                <p
                                    className="mt-3 text-sm italic text-[#F9F9F7]/65"
                                    data-testid="home-free-consultation"
                                >
                                    {t.services.freeConsultation}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => openBooking(navigate)}
                                    className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-bold link-underline"
                                    data-testid="services-cta-call"
                                >
                                    {t.services.beginCta}
                                    <ArrowUpRight size={16} strokeWidth={1.5} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* COMPLEMENTARY services */}
            <section className="py-20 md:py-24 border-t hair" data-testid="home-adjacent">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-y-8 md:gap-x-8">
                    <div className="col-span-12 md:col-span-3">
                        <p className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "#9B7A58" }}>
                            {t.services.adjacentEyebrow}
                        </p>
                        <h2 className="font-display mt-6 text-3xl md:text-4xl tracking-tight font-medium" style={{ letterSpacing: "-0.03em" }}>
                            {t.services.adjacentTitle}
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-5 md:col-start-5">
                        <p className="text-[#1C1C1C]/80 leading-relaxed text-base md:text-lg">
                            {t.services.adjacentBody}
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-3 md:col-start-10">
                        <p className="eyebrow">{t.services.adjacentListEyebrow}</p>
                        <ul className="mt-5 space-y-3 text-sm">
                            {t.services.adjacent.map((it) => (
                                <li key={it} className="flex items-start gap-3">
                                    <span className="mt-2 inline-block w-1.5 h-1.5 shrink-0" style={{ background: "#9B7A58" }} />
                                    <span className="text-[#1C1C1C]/80 leading-relaxed">{it}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* ABOUT preview */}
            <section className="py-24 md:py-32 border-t hair bg-[#F4F3EF]" data-testid="about-preview">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                    <div className="grid grid-cols-12 gap-y-10 md:gap-x-12 items-center">
                        <div className="col-span-12 md:col-span-5">
                            <div className="aspect-[4/5] overflow-hidden">
                                <img src={ZURICH_IMG} alt="Zurich, Grossmünster and the Limmat river" className="w-full h-full object-cover" />
                            </div>
                        </div>
                        <div className="col-span-12 md:col-span-6 md:col-start-7">
                            <p className="eyebrow">{t.home.aboutPreviewEyebrow}</p>
                            <h2
                                className="font-display mt-5 text-4xl sm:text-5xl tracking-tight font-medium"
                                style={{ letterSpacing: "-0.03em" }}
                            >
                                {t.home.aboutPreviewTitle}
                            </h2>
                            <p className="mt-8 text-[#1C1C1C]/75 leading-relaxed text-base md:text-lg max-w-xl">
                                {t.home.aboutPreviewBody}
                            </p>
                            <Link
                                to="/about"
                                className="mt-10 link-underline inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-bold"
                                data-testid="about-cta"
                            >
                                {t.home.aboutPreviewCta}
                                <ArrowRight size={14} strokeWidth={1.5} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* INSIGHTS preview */}
            <section className="py-24 md:py-32" data-testid="insights-preview">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                    <div className="flex items-end justify-between mb-12 gap-6">
                        <div>
                            <p className="eyebrow">{t.insights.eyebrow}</p>
                            <h2
                                className="font-display mt-5 text-4xl sm:text-5xl tracking-tight font-medium"
                                style={{ letterSpacing: "-0.03em" }}
                            >
                                {t.home.insightsTitle}
                            </h2>
                        </div>
                        <Link
                            to="/insights"
                            className="link-underline text-[12px] uppercase tracking-[0.22em] font-bold"
                            data-testid="insights-view-all"
                        >
                            {t.common.allInsights}
                        </Link>
                    </div>

                    <ul className="border-t hair">
                        {PREVIEW_SLUGS.map((slug) => {
                            const p = pm[slug];
                            return (
                                <li key={slug} className="border-b hair">
                                    <Link
                                        to={`/insights/${slug}`}
                                        className="group flex items-center justify-between gap-6 py-7 hover:bg-white px-2 -mx-2 transition-colors"
                                        data-testid={`insight-row-${slug}`}
                                    >
                                        <div className="flex items-center gap-6 md:gap-10 min-w-0">
                                            <span
                                                className="text-[10px] uppercase tracking-[0.22em] font-bold w-20 shrink-0"
                                                style={{ color: "#9B7A58" }}
                                            >
                                                {p.cat}
                                            </span>
                                            <span
                                                className="font-display text-xl md:text-2xl tracking-tight font-medium truncate"
                                                style={{ letterSpacing: "-0.02em" }}
                                            >
                                                {p.title}
                                            </span>
                                        </div>
                                        <div className="hidden sm:flex items-center gap-6 shrink-0">
                                            <span className="text-xs text-[#1C1C1C]/50">{p.date}</span>
                                            <ArrowUpRight
                                                size={18}
                                                strokeWidth={1.5}
                                                className="text-[#1C1C1C]/30 group-hover:text-[#1C1C1C] transition-colors"
                                            />
                                        </div>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </section>
        </div>
    );
}
